import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "@tanstack/react-router";
import { Search, ArrowRight, Clock, Package } from "lucide-react";
import {
  CommandDialog, CommandInput, CommandList, CommandGroup, CommandItem, CommandSeparator,
} from "@/components/ui/command";
import { StatusBadge } from "@/components/orders/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useSession } from "@/hooks/use-auth";
import { hasRole } from "@/lib/roles";
import { useActiveStore } from "@/hooks/use-active-store";
import { useActiveStoreType } from "@/hooks/use-active-store-type";
import { useStoreSyncStatus, useStores } from "@/hooks/use-stores";
import { useOrders } from "@/hooks/use-orders";
import { useDebounce } from "@/hooks/use-debounce";
import { formatCurrency } from "@/lib/utils";
import { allNavItems, matchesQuery, type NavItem } from "./nav-config";
import { readRecentRoutes, rememberRoute, onOpenPalette, isPaletteShortcut, paletteEnterAllowed } from "./nav-state";

/** A bare order number the user can jump to without waiting on search. */
function directOrderNumber(query: string): string | null {
  const q = query.trim();
  return /^\d{1,8}$/.test(q) ? q : null;
}

function OrderRow({ order, onSelect }: { order: any; onSelect: () => void }) {
  const date = order.date_created ? new Date(order.date_created).toLocaleDateString("bs-BA") : "";
  return (
    <CommandItem value={`order-${order.id}`} onSelect={onSelect} className="gap-2">
      <Package className="size-4" />
      <span className="font-medium tabular-nums">#{order.number}</span>
      <span className="truncate text-muted-foreground">{order.customer_name || "—"}</span>
      <span className="ml-auto flex shrink-0 items-center gap-2">
        <span className="tabular-nums text-muted-foreground">
          {formatCurrency(Number(order.total) || 0, order.currency)}
        </span>
        <StatusBadge status={order.status} />
        <span className="hidden tabular-nums text-muted-foreground sm:inline">{date}</span>
      </span>
    </CommandItem>
  );
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const pathname = useLocation({ select: (l) => l.pathname });
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;
  // Enter potvrđuje stavku tek kad je korisnik kucao ili koristio strelice —
  // skenerov Enter odmah po otvaranju ne smije odabrati prvu stavku iz "Nedavno".
  const armedRef = useRef(false);
  const { user } = useSession();
  const isAdmin = hasRole(user, "admin");
  const { storeId } = useActiveStore();
  const storeType = useActiveStoreType();
  const isDev = hasRole(user, "dev");
  const navCtx = { isAdmin: !!isAdmin, isDev, storeType };
  const { data: syncStatus } = useStoreSyncStatus(storeId);
  const { data: stores } = useStores();

  const storeName = stores?.find((s: any) => s.id === storeId)?.name ?? "";
  const isWebhookStore = syncStatus?.syncMode === "webhook";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isPaletteShortcut(e, pathnameRef.current)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", onKey);
    const unsubscribe = onOpenPalette(() => setOpen(true));
    return () => {
      document.removeEventListener("keydown", onKey);
      unsubscribe();
    };
  }, []);

  // Reset the query on close so reopening never shows a stale result set.
  useEffect(() => {
    if (!open) setQuery("");
    armedRef.current = false;
  }, [open]);

  const onCommandKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Home" || e.key === "End") {
        armedRef.current = true;
        return;
      }
      if (e.key === "Enter" && !paletteEnterAllowed({ query, armed: armedRef.current })) {
        e.preventDefault();
      }
    },
    [query]
  );

  const go = useCallback(
    (to: string) => {
      rememberRoute(to);
      setOpen(false);
      navigate({ to: to as any });
    },
    [navigate]
  );

  const items = useMemo(
    () => (storeType ? allNavItems({ isAdmin: navCtx.isAdmin, isDev, storeType }) : []),
    [navCtx.isAdmin, isDev, storeType],
  );
  const pageMatches = useMemo(
    () => (query.trim() ? items.filter((i) => matchesQuery(i, query)) : []),
    [items, query]
  );
  const recentItems = useMemo(() => {
    if (query.trim()) return [];
    const recent = readRecentRoutes();
    return recent
      .map((to) => items.find((i) => i.to === to))
      .filter((i): i is NavItem => !!i);
  }, [items, query, open]);

  const debouncedQuery = useDebounce(query.trim(), 300);
  const orderSearchEnabled = isWebhookStore && debouncedQuery.length >= 2;
  const { data: orderData, isFetching: ordersLoading } = useOrders(
    orderSearchEnabled ? storeId : null,
    { search: debouncedQuery, per_page: "8" }
  );
  const orders: any[] = orderSearchEnabled ? orderData?.orders ?? [] : [];

  const jumpNumber = directOrderNumber(query);
  // Typing is ahead of the debounce whenever the two differ — show skeletons, not "nothing found".
  const ordersPending = orderSearchEnabled && (ordersLoading || debouncedQuery !== query.trim());

  return (
    <CommandDialog
      open={open}
      onOpenChange={setOpen}
      title="Pretraga"
      description="Pretraži stranice i narudžbe"
      shouldFilter={false}
      onKeyDown={onCommandKeyDown}
      className="sm:max-w-2xl"
    >
      <CommandInput
        placeholder="Traži stranicu, broj narudžbe, kupca ili tracking kod…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList className="max-h-[420px]">
        {jumpNumber && (
          <CommandGroup>
            <CommandItem value="jump-order" onSelect={() => go(`/orders/${jumpNumber}`)}>
              <ArrowRight className="size-4" />
              Otvori narudžbu #{jumpNumber}
            </CommandItem>
          </CommandGroup>
        )}

        {recentItems.length > 0 && (
          <CommandGroup heading="Nedavno">
            {recentItems.map((item) => (
              <CommandItem key={`recent-${item.to}`} value={`recent-${item.to}`} onSelect={() => go(item.to)}>
                <Clock className="size-4" />
                {item.label}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandGroup heading={query.trim() ? "Stranice" : "Sve stranice"}>
          {(query.trim() ? pageMatches : items).map((item) => (
            <CommandItem key={item.to} value={item.to} onSelect={() => go(item.to)}>
              <item.icon className="size-4" />
              {item.label}
            </CommandItem>
          ))}
        </CommandGroup>

        {query.trim() && (
          <>
            <CommandSeparator />
            {isWebhookStore ? (
              <CommandGroup heading={storeName ? `Narudžbe · ${storeName}` : "Narudžbe"}>
                {ordersPending &&
                  [0, 1, 2].map((i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1.5">
                      <Skeleton className="size-4 rounded-sm" />
                      <Skeleton className="h-4 w-16" />
                      <Skeleton className="h-4 w-32" />
                    </div>
                  ))}
                {!ordersPending &&
                  orders.map((order) => (
                    <OrderRow key={order.id} order={order} onSelect={() => go(`/orders/${order.id}`)} />
                  ))}
                {!ordersPending && orders.length === 0 && debouncedQuery.length >= 2 && (
                  <p className="px-2 py-3 text-sm text-muted-foreground">
                    Nema narudžbi za «{debouncedQuery}»
                  </p>
                )}
                <CommandItem
                  value="open-orders-search"
                  onSelect={() => go(`/orders?search=${encodeURIComponent(query.trim())}`)}
                >
                  <Search className="size-4" />
                  Otvori pretragu u Narudžbama
                </CommandItem>
              </CommandGroup>
            ) : (
              <p className="px-4 py-3 text-xs text-muted-foreground">
                Pretraga narudžbi dostupna samo uz webhook sinhronizaciju.
              </p>
            )}
          </>
        )}

        {query.trim() && pageMatches.length === 0 && !isWebhookStore && (
          <p className="px-4 pb-4 text-sm text-muted-foreground">Nema rezultata.</p>
        )}
      </CommandList>
    </CommandDialog>
  );
}
